import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, ArrowRight, ArrowUpRight, Sparkles, Building2, Users,
  UserCheck, Briefcase, Crown, BarChart3, Heart, MapPin, Compass,
  Workflow, ShieldCheck, Layers, Target, Check
} from "lucide-react";
import { EcosystemSection } from "@/components/EcosystemSection";
import { Breadcrumbs } from "@/components/Breadcrumbs";

const AUDIENCES = [
  { id: "buyer",  icon: UserCheck,  label: "Compradores",
    headline: "Tu hogar ideal te está esperando.",
    desc: "Responde 52 preguntas y descubre solo propiedades con +70% de compatibilidad real con tu vida.",
    cta: "Empezar gratis", goto: "/comprador",
    points: ["ADN de Hogar personalizado", "Solo matches relevantes", "Contacto directo con agente"],
    accent: "from-[#CCD500] to-[#9DAB00]" },
  { id: "agent",  icon: Briefcase,  label: "Agentes",
    headline: "Multiplica tus cierres con IA.",
    desc: "8 módulos de inteligencia comercial: valuación, terreno, RIC, gap, audiencias, funnel y más.",
    cta: "Probar 7 días", goto: "/auth",
    points: ["Leads pre-calificados", "Valuación MVIP en 1 click", "Funnel y analytics en vivo"],
    accent: "from-cyan-400 to-blue-600" },
  { id: "owner",  icon: Building2,  label: "Propietarios",
    headline: "Vende más rápido y seguro.",
    desc: "Lista privadamente, elige tu agente, y recibe solo leads con alta intención de compra.",
    cta: "Registrar propiedad", goto: "/auth",
    points: ["Tus datos protegidos", "Elige al agente que te representa", "Match Score por interesado"],
    accent: "from-fuchsia-400 to-rose-600" },
  { id: "mp",     icon: Crown,      label: "Master Partners",
    headline: "Domina tu territorio exclusivo.",
    desc: "Conviértete en líder de mercado por ciudad con beneficios premium y red de agentes asignados.",
    cta: "Postular como MP", goto: "/auth",
    points: ["Territorio exclusivo por ciudad", "Red de agentes asignados", "Branding y landing dedicada"],
    accent: "from-amber-300 to-yellow-600" },
];

const MODULES_TEASER = [
  { id: "mvip",    name: "MVIP",      desc: "Valuación inteligente",        icon: BarChart3 },
  { id: "terreno", name: "Terreno",   desc: "Potencial de desarrollo",      icon: Layers },
  { id: "match",   name: "Match",     desc: "Afinidad de 52 puntos",        icon: Sparkles },
  { id: "ric",     name: "RIC",       desc: "Retorno de inversión",         icon: Target },
  { id: "gap",     name: "Gap",       desc: "Brecha de oportunidad",        icon: Compass },
  { id: "aud",     name: "Audiencias", desc: "Segmentación hiper-dirigida", icon: Users },
  { id: "funnel",  name: "Funnel",    desc: "Control de embudo",            icon: Workflow },
  { id: "boveda",  name: "Bóveda",    desc: "Documentos legales",           icon: ShieldCheck },
];

const ADVANTAGES = [
  { icon: Sparkles,    title: "Leads pre-calificados", desc: "Solo recibes contactos con 70%+ de compatibilidad. Olvídate de perder horas con prospectos fríos." },
  { icon: BarChart3,   title: "Valuaciones MVIP",       desc: "Genera dictámenes técnicos profesionales con IA en menos de 30 segundos. PDF firmado digitalmente." },
  { icon: Layers,      title: "Potencial de Terreno",   desc: "Analiza 4 escenarios de desarrollo con ROI, costos notariales mexicanos y recomendación basada en datos." },
  { icon: Workflow,    title: "CRM y Funnel",           desc: "Pipeline visual de 4 fases con drag & drop, recordatorios automáticos y métricas por etapa." },
  { icon: ShieldCheck, title: "Bóveda Legal",           desc: "Almacenamiento cifrado de contratos, identificaciones y escrituras con bitácora de auditoría." },
  { icon: Crown,       title: "Branding Personal",      desc: "Master Partners obtienen landing dedicada y territorio exclusivo por ciudad." },
];

const Eyebrow = ({ children }) => (
  <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#CCD500]">
    <span className="h-px w-8 bg-[#CCD500]/60" /> {children}
  </span>
);

export const AgentLandingPage = () => {
  const navigate = useNavigate();
  const [activeAudience, setActiveAudience] = useState("agent");
  const current = AUDIENCES.find((a) => a.id === activeAudience);

  return (
    <div className="min-h-screen bg-[#0B1120] text-slate-100 font-body overflow-x-hidden selection:bg-[#CCD500] selection:text-black">
      {/* Header */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#0B1120]/70 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} data-testid="ag-back-btn" className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Volver al inicio</span>
          </button>
          <img src="https://customer-assets.emergentagent.com/job_propswipe-7/artifacts/65kacgp6_Isotipo%20Findperty-2.png" alt="Findperty" className="h-8 brightness-0 invert" />
          <Button onClick={() => navigate("/auth")} className="bg-[#CCD500] text-black font-bold hover:bg-[#E5FF00] hover:scale-105 transition-all rounded-full px-5" data-testid="ag-header-cta">
            Probar 7 días
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-24 md:pt-28 pb-20 px-6 lg:px-8 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-[#CCD500]/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-40 -right-40 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto">
          <Breadcrumbs items={[{ label: "Conviértete en Agente Findperty" }]} className="mb-8" />
        </div>
        <div className="relative max-w-5xl mx-auto text-center">
          <Eyebrow>Conviértete en Agente Findperty</Eyebrow>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] mt-6" data-testid="ag-hero-title">
            Multiplica tus cierres con{" "}
            <span className="bg-gradient-to-r from-[#CCD500] to-[#E5FF00] bg-clip-text text-transparent">inteligencia artificial</span>.
          </h1>
          <p className="mt-7 text-lg md:text-xl text-slate-400 max-w-2xl mx-auto">
            Un ecosistema completo de IA para agentes inmobiliarios mexicanos. 8 módulos profesionales, leads pre-calificados y branding personal.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <Button size="lg" onClick={() => navigate("/auth")} data-testid="ag-hero-primary-cta"
              className="bg-[#CCD500] text-black font-bold hover:bg-[#E5FF00] hover:scale-105 transition-all shadow-[0_0_30px_rgba(204,213,0,0.4)] rounded-full px-7 h-14 text-base">
              Probar 7 días gratis
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate("/demo")} data-testid="ag-hero-demo-cta"
              className="bg-white/5 text-white border-white/20 hover:bg-white/10 hover:border-white/40 rounded-full px-7 h-14 text-base backdrop-blur-md">
              Ver demo del Agente
            </Button>
          </div>
        </div>
      </section>

      {/* AUDIENCES */}
      <section className="relative py-20 px-6 lg:px-8 bg-gradient-to-b from-transparent via-slate-950/40 to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <Eyebrow>Para todos los actores del mercado</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-4 tracking-tight">
              Una plataforma. <span className="text-[#CCD500]">Cuatro audiencias.</span>
            </h2>
            <p className="mt-4 text-slate-400">
              Findperty fue diseñada para conectar compradores, agentes, propietarios y master partners en un solo ecosistema.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {AUDIENCES.map((a) => (
              <button key={a.id} onClick={() => setActiveAudience(a.id)} data-testid={`ag-audience-${a.id}`}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all border ${
                  activeAudience === a.id
                    ? "bg-[#CCD500] text-black border-[#CCD500] shadow-[0_0_25px_rgba(204,213,0,0.4)]"
                    : "bg-white/5 text-slate-300 border-white/10 hover:bg-white/10 hover:text-white"}`}>
                <a.icon className="w-4 h-4 inline mr-2 -mt-0.5" />
                {a.label}
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div key={current.id}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.35 }}
              className="grid md:grid-cols-2 gap-8 rounded-3xl bg-slate-900/60 backdrop-blur-md border border-white/10 p-8 md:p-12">
              <div>
                <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${current.accent} mb-6 shadow-lg`}>
                  <current.icon className="w-7 h-7 text-black" />
                </div>
                <h3 className="font-display text-3xl md:text-4xl font-bold text-white tracking-tight leading-tight">{current.headline}</h3>
                <p className="mt-4 text-slate-400 text-lg">{current.desc}</p>
                <Button onClick={() => navigate(current.goto)} data-testid={`ag-audience-cta-${current.id}`}
                  className="mt-7 bg-[#CCD500] text-black font-bold hover:bg-[#E5FF00] hover:scale-105 rounded-full px-7 h-12">
                  {current.cta}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
              <div className="space-y-3">
                {current.points.map((point, i) => (
                  <motion.div key={point} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.08 }}
                    className="flex items-start gap-3 p-4 rounded-2xl bg-slate-950/50 border border-white/5">
                    <div className="w-8 h-8 rounded-lg bg-[#CCD500]/10 border border-[#CCD500]/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-[#CCD500]" />
                    </div>
                    <p className="text-slate-200">{point}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ECOSYSTEM */}
      <EcosystemSection />

      {/* AGENT MODULES TEASER */}
      <section className="relative py-24 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <Eyebrow>Para Agentes & Master Partners</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-4 tracking-tight">
              8 módulos que convierten <span className="text-[#CCD500]">datos en cierres</span>.
            </h2>
            <p className="mt-5 text-slate-400">
              MVIP, Terreno, Match, RIC, Gap, Audiencias, Funnel y Bóveda — un arsenal de IA listo para producir reportes y leads en minutos.
            </p>
            <Button onClick={() => navigate("/demo")} data-testid="ag-modules-explore-cta" className="mt-7 bg-white text-black hover:bg-[#CCD500] rounded-full px-6">
              Explorar los 8 módulos
              <ArrowUpRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {MODULES_TEASER.map((m, i) => (
              <motion.div key={m.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                onClick={() => navigate("/demo")} data-testid={`ag-module-${m.id}`}
                className="group cursor-pointer relative rounded-2xl bg-slate-900/60 border border-white/10 p-4 hover:border-[#CCD500]/40 hover:-translate-y-1 transition-all">
                <m.icon className="w-5 h-5 text-[#CCD500] mb-3" />
                <p className="font-display text-base font-bold text-white">{m.name}</p>
                <p className="text-xs text-slate-400 mt-1 leading-snug">{m.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ADVANTAGES */}
      <section className="py-24 px-6 lg:px-8 bg-gradient-to-b from-transparent via-slate-950/40 to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <Eyebrow>Por qué Findperty</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-4 tracking-tight">
              Ventajas que <span className="text-[#CCD500]">transforman tu carrera</span>.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {ADVANTAGES.map((a, i) => (
              <motion.div key={a.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className="rounded-3xl bg-slate-900/60 backdrop-blur-md border border-white/10 p-6 hover:border-[#CCD500]/40 transition-all">
                <div className="w-11 h-11 rounded-2xl bg-[#CCD500]/10 border border-[#CCD500]/30 flex items-center justify-center mb-4">
                  <a.icon className="w-5 h-5 text-[#CCD500]" />
                </div>
                <h3 className="font-display text-xl font-bold text-white mb-2">{a.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{a.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-6 lg:px-8 pb-24">
        <div className="max-w-5xl mx-auto rounded-3xl bg-gradient-to-br from-[#CCD500] to-[#9DAB00] p-10 md:p-16 text-center relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-white/20 blur-3xl" />
          <div className="relative">
            <Briefcase className="w-10 h-10 text-black mx-auto mb-4" />
            <h2 className="font-display text-4xl md:text-6xl font-black text-black tracking-tighter mb-4 leading-[0.95]">
              ¿Listo para crecer?
            </h2>
            <p className="text-black/70 text-lg max-w-xl mx-auto mb-8">
              7 días de prueba gratis. Sin tarjeta de crédito. Configura tu cuenta en 5 minutos.
            </p>
            <Button size="lg" onClick={() => navigate("/auth")} data-testid="ag-final-cta"
              className="bg-black text-[#CCD500] font-bold hover:bg-slate-900 hover:scale-105 rounded-full px-8 h-14 text-base">
              Crear cuenta de Agente
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 px-6 lg:px-8 py-10">
        <div className="max-w-7xl mx-auto text-center text-sm text-slate-500">
          © 2026 Findperty · Hecho para agentes que cierran más.
        </div>
      </footer>
    </div>
  );
};

export default AgentLandingPage;
